import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiMinus,
  FiPlus,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";

import {
  CartItem,
  CurrentPrice,
  GroupHeader,
  ItemBand,
  ItemImage,
  ItemInfo,
  ItemName,
  ItemPrice,
  ItemSize,
  PageBtn,
  Pagination,
  PerUnitPrice,
  PriceQtyStack,
  QuantityBtn,
  QuantityControl,
  QuantityInput,
  RemoveBtn,
} from "../CartPage.styles";
import { formatPrice, getPaginationRange } from "../../../../utils/utils";
import CursorImagePreview from "../../../../components/utils/CursorImagePreview";

/* Ako naziv počinje imenom benda ("Brkovi majica – Brkovi"), skini duplikat */
const trimOwnerPrefix = (name, ownerName) => {
  if (!name || !ownerName) return name;
  if (name.toLowerCase().startsWith(ownerName.toLowerCase())) {
    const trimmed = name.slice(ownerName.length).replace(/^[\s–—-]+/, "");
    return trimmed.length > 2
      ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
      : name;
  }
  return name;
};

export default function CartItemsList({
  items,
  totalPages,
  currentPage,
  onPageChange,
  groupTitle,
  showGroupHeader = false, // header samo kad postoji 2+ grupa (v2 s.9.2)
  onUpdateQuantity,
  onRemoveItem,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const [imgPreview, setImgPreview] = useState(null);

  const goToOwner = (item) => {
    const path = item.band?.id
      ? `/bend/${item.band.slug || item.band.id}`
      : item.festival?.id
        ? `/festival/${item.festival.slug || item.festival.id}`
        : null;
    if (path) {
      navigate(path);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 300);
    }
  };

  return (
    <div>
      {showGroupHeader && (
        <GroupHeader>
          <FiTruck size={14} />
          {groupTitle}
        </GroupHeader>
      )}

      {/* Bez AnimatePresence: exit animacija je pri promeni stranice držala
          obe strane u DOM-u (dupla visina pa skupljanje). Ulaz ostaje animiran,
          layout prop glatko podigne preostale stavke posle brisanja. */}
      {items?.map((item) => {
        const ownerName = item.band?.name || item.festival?.name || "";
        const unitPrice = item.salePrice || item.price;
        const lineTotal = unitPrice * item.quantity;

        return (
          <CartItem
            key={item.cartItemId}
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            <ItemImage
              onMouseEnter={(e) =>
                setImgPreview({
                  src: item.mainImage,
                  x: e.clientX,
                  y: e.clientY,
                })
              }
              onMouseMove={(e) =>
                setImgPreview((p) =>
                  p ? { ...p, x: e.clientX, y: e.clientY } : p,
                )
              }
              onMouseLeave={() => setImgPreview(null)}
            >
              <img
                src={item.mainImage}
                alt={item.name}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    ownerName?.substring(0, 3) || "IMG";
                }}
              />
            </ItemImage>
            <ItemInfo>
              {ownerName && (
                <ItemBand
                  as="button"
                  type="button"
                  onClick={() => goToOwner(item)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {ownerName}
                </ItemBand>
              )}
              <ItemName>{trimOwnerPrefix(item.name, ownerName)}</ItemName>
              {item.selectedSize && (
                <ItemSize>Veličina: {item.selectedSize}</ItemSize>
              )}
            </ItemInfo>

            {/* Cena iznad qty steppera · qty>1: line total + cena/kom */}
            <PriceQtyStack>
              <ItemPrice>
                <CurrentPrice>{formatPrice(lineTotal)}</CurrentPrice>
                {item.quantity > 1 && (
                  <PerUnitPrice>{formatPrice(unitPrice)} / kom</PerUnitPrice>
                )}
              </ItemPrice>
              {!readOnly && (
                <QuantityControl>
                  <QuantityBtn
                    type="button"
                    title={item.quantity <= 1 ? "Ukloni proizvod" : "Smanji"}
                    onClick={() => {
                      // minus na qty=1 = uklanjanje (sa undo toastom u parentu)
                      if (item.quantity <= 1) {
                        onRemoveItem(item.cartItemId);
                      } else {
                        onUpdateQuantity(item.cartItemId, item.quantity - 1);
                      }
                    }}
                  >
                    <FiMinus />
                  </QuantityBtn>
                  <QuantityInput
                    value={item.quantity}
                    onChange={(e) =>
                      onUpdateQuantity(
                        item.cartItemId,
                        parseInt(e.target.value) || 1,
                      )
                    }
                    min="1"
                    max={item.stockQuantity}
                  />
                  <QuantityBtn
                    type="button"
                    title="Povećaj"
                    onClick={() =>
                      onUpdateQuantity(item.cartItemId, item.quantity + 1)
                    }
                    disabled={item.quantity >= item.stockQuantity}
                  >
                    <FiPlus />
                  </QuantityBtn>
                </QuantityControl>
              )}
              {readOnly && <ItemSize>Količina: {item.quantity}</ItemSize>}
            </PriceQtyStack>

            {!readOnly && (
              <RemoveBtn
                type="button"
                title="Ukloni proizvod"
                onClick={() => onRemoveItem(item.cartItemId)}
              >
                <FiTrash2 size={16} />
              </RemoveBtn>
            )}
          </CartItem>
        );
      })}

      {totalPages > 1 && (
        <Pagination>
          <PageBtn
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <FiChevronLeft />
          </PageBtn>

          {getPaginationRange(currentPage, totalPages).map((page, index) =>
            page === "..." ? (
              <PageBtn
                key={`dots-${index}`}
                disabled
                style={{ cursor: "default", border: "none" }}
              >
                …
              </PageBtn>
            ) : (
              <PageBtn
                key={page}
                $active={currentPage === page}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PageBtn>
            ),
          )}

          <PageBtn
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            <FiChevronRight />
          </PageBtn>
        </Pagination>
      )}

      <CursorImagePreview preview={imgPreview} size={360} />
    </div>
  );
}
